import ApiService from "../../services/ApiService";
import Logger from "../../services/Logger";

/** Studio games shown on the notification Apps list (not hints / points / guide wallets). */
const PRIMARY_NOTIFICATION_APP_CODES = new Set([
    "sliding_puzzle_quest",
    "jigsaw_muse",
    "word_wheel_quest",
]);

function isPrimaryNotificationApp(app) {
    const code = typeof app?.code === "string" ? app.code.trim().toLowerCase() : "";
    return PRIMARY_NOTIFICATION_APP_CODES.has(code);
}

const AdminNotificationService = {
    listApps: async () => {
        try {
            const response = await ApiService.getData("/v1/push/apps");
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to load apps",
                };
            }
            const content = Array.isArray(response?.content)
                ? response.content.filter(isPrimaryNotificationApp)
                : [];
            return {
                success: true,
                data: {
                    ...response,
                    content,
                },
            };
        } catch (error) {
            Logger.error("Error fetching push apps:", error);
            return {
                success: false,
                error: error.message || "Failed to load apps",
            };
        }
    },

    getOverview: async (appCode = "") => {
        try {
            const params = {};
            if (appCode) {
                params.appCode = appCode;
            }
            const response = await ApiService.getData("/v1/push/overview", Object.keys(params).length ? params : undefined);
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to load overview",
                };
            }
            return {
                success: true,
                data: response,
            };
        } catch (error) {
            Logger.error("Error fetching push overview:", error);
            return {
                success: false,
                error: error.message || "Failed to load overview",
            };
        }
    },

    listNotifications: async (page = 0, pageSize = 25, sort = "createdAt:desc", appCode = "") => {
        try {
            const params = { page, pageSize, sort };
            if (appCode) {
                params.appCode = appCode;
            }
            const response = await ApiService.getData("/v1/push/notifications", params);
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to load notifications",
                };
            }
            return {
                success: true,
                data: response,
            };
        } catch (error) {
            Logger.error("Error fetching push notifications:", error);
            return {
                success: false,
                error: error.message || "Failed to load notifications",
            };
        }
    },

    /**
     * DELETE /v1/push/notifications — body `{ ids: string[] }`.
     */
    deleteByIds: async (ids) => {
        try {
            const unique = [];
            const seen = new Set();
            for (const id of ids || []) {
                if (id == null) continue;
                const s = String(id).trim();
                if (!s || seen.has(s)) continue;
                seen.add(s);
                unique.push(s);
            }
            if (unique.length === 0) {
                return { success: false, error: "No log ids selected" };
            }
            const response = await ApiService.deleteData("/v1/push/notifications", { ids: unique });
            if (response?.code === "FAILURE") {
                return { success: false, error: response.message || "Failed to delete logs" };
            }
            return { success: true, data: response };
        } catch (error) {
            Logger.error("Error deleting push notifications:", error);
            return {
                success: false,
                error: error.message || "Failed to delete logs",
            };
        }
    },

    setBatchSize: async (appCode, batchSize) => {
        try {
            const response = await ApiService.putData("/v1/push/batch-size", {
                appCode,
                batchSize: Number(batchSize),
            });
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to update batch limit",
                };
            }
            return { success: true, data: response };
        } catch (error) {
            Logger.error("Error updating push batch limit:", error);
            return {
                success: false,
                error: error.message || "Failed to update batch limit",
            };
        }
    },

    setMemo: async (appCode, memo) => {
        try {
            const response = await ApiService.putData("/v1/push/memo", {
                appCode,
                memo: memo == null ? "" : String(memo),
            });
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to save memo",
                };
            }
            return { success: true, data: response };
        } catch (error) {
            Logger.error("Error saving notification memo:", error);
            return {
                success: false,
                error: error.message || "Failed to save memo",
            };
        }
    },

    setDailyGiftTestOnly: async (testOnly) => {
        try {
            const response = await ApiService.putData("/v1/push/daily-gift-test-only", {
                testOnly: Boolean(testOnly),
            });
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to update test-account option",
                };
            }
            return { success: true, data: response };
        } catch (error) {
            Logger.error("Error updating daily gift test-account option:", error);
            return {
                success: false,
                error: error.message || "Failed to update test-account option",
            };
        }
    },

    sendDailyGiftTest: async () => {
        try {
            const response = await ApiService.postData("/v1/push/daily-gift-test", {});
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to send test notification",
                };
            }
            return { success: true, data: response };
        } catch (error) {
            Logger.error("Error sending daily gift test notification:", error);
            return {
                success: false,
                error: error.message || "Failed to send test notification",
            };
        }
    },

    setDailyGiftEnabled: async (enabled) => {
        try {
            const response = await ApiService.putData("/v1/push/daily-gift-enabled", {
                enabled: Boolean(enabled),
            });
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to update daily gift",
                };
            }
            return { success: true, data: response };
        } catch (error) {
            Logger.error("Error updating daily gift switch:", error);
            return {
                success: false,
                error: error.message || "Failed to update daily gift",
            };
        }
    },

    setSendEnabled: async (appCode, enabled) => {
        try {
            const response = await ApiService.putData("/v1/push/send-enabled", { appCode, enabled: Boolean(enabled) });
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to update send switch",
                };
            }
            return { success: true, data: response };
        } catch (error) {
            Logger.error("Error updating push send switch:", error);
            return {
                success: false,
                error: error.message || "Failed to update send switch",
            };
        }
    },

    send: async ({ appCode, title, body }) => {
        try {
            const response = await ApiService.postData("/v1/push/send", { appCode, title, body });
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to send notification",
                };
            }
            return {
                success: true,
                data: response,
            };
        } catch (error) {
            Logger.error("Error sending push notification:", error);
            return {
                success: false,
                error: error.message || "Failed to send notification",
            };
        }
    },
};

export default AdminNotificationService;
